/**
 * manage-handlers.js
 */

import React from 'react';
import {
  useMediaQuery,
  Link,
  Tooltip,
  List,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import DataGrid from 'components/data-grid';
import HandlerRowMobile from './handler-row-mobile';
import BarChart from './bar-chart';
import {
  NameContent,
  Menu,
  handlerLateRecordsPath,
  handlerTrainingPath,
  handlerTrainingDetectionPath,
  handlerTrainingPatrolPath,
  handlerDeploymentPath
} from './handler-shared';


export default function ManageHandlers(props) {
  const {
    data,
    onRemove,
    isAllExpanded
  } = props;
  const { user } = React.useContext(UserContext);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  

  const linkBuilder = (rowParams, tooltipSuffix, pathname) => {
    return (
      <Tooltip arrow placement='top'
        title={`View ${rowParams.row.id === user.id ? 'my' : `${rowParams.row.name}'s`} ${tooltipSuffix}`}
      >
        <Link
          component={RouterLink}
          to={pathname}
          children={rowParams.value}
          underline="hover" />
      </Tooltip>
    );
  }


  const fieldDefs = {
    // NAME
    name: {
      field: 'name',
      headerName: 'Name',
      width: 190,
      renderCell: (params) => <NameContent member={params.row} />
    },

    // LATE RECORDS COUNT
    lateRecordsCount: {
      field: 'recentLateRecordsCount',
      type: 'number',
      headerName: 'Late\u000ARecords',
      headerAlign: 'center',
      description: 'Late records in the last 3 months',
      align: 'center',
      width: 75,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'late records', handlerLateRecordsPath(params.row.id))
    },

    // TRAINING HOURS COUNT
    trainingHoursCount: {
      field: 'recentTrainingHoursCount',
      type: 'number',
      headerName: 'Training\u000AHours',
      headerAlign: 'center',
      description: 'Training hours in the last 3 months',
      align: 'center',
      width: 75,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'training records', handlerTrainingPath(params.row.id))
    },

    // EVENTS ATTENDED COUNT
    eventsAttendedCount: {
      field: 'recentEventsAttendedCount',
      type: 'number',
      headerName: 'Events\u000AAttended',
      headerAlign: 'center',
      description: 'Events attended in the last 3 months',
      align: 'center',
      width: 80,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'training records', handlerTrainingPath(params.row.id))
    },

    // DETECTION EXERCISES COUNT
    detectionExercisesCount: {
      field: 'recentDetectionExercisesCount',
      type: 'number',
      headerName: 'Detection\u000AExercises',
      headerAlign: 'center',
      description: 'Detection exercises completed in the last 3 months',
      align: 'center',
      width: 85,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'detection training records', handlerTrainingDetectionPath(params.row.id))
    },

    // PATROL EXERCISES COUNT
    patrolExercisesCount: {
      field: 'recentPatrolExercisesCount',
      type: 'number',
      headerName: 'Patrol\u000AExercises',
      headerAlign: 'center',
      description: 'Patrol exercises completed in the last 3 months',
      align: 'center',
      width: 85,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'patrol training records', handlerTrainingPatrolPath(params.row.id))
    },

    // DEPLOYMENTS COUNT
    deploymentsCount: {
      field: 'recentDeploymentsCount',
      type: 'number',
      headerName: 'Total\u000ADeploys',
      headerAlign: 'center',
      description: 'Deployments completed in the last 3 months',
      align: 'center',
      width: 75,
      headerClassName: 'small-header no-padding',
      cellClassName: 'simple-count-cell',
      renderCell: params => linkBuilder(params, 'deployment records', handlerDeploymentPath(params.row.id))
    },

    // TRAINING BY MONTH
    trainingByMonth: {
      field: 'trainingByMonth',
      headerName: 'Training By Month',
      width: user.role.isSupervisor ? 232 : 285,
      cellClassName: 'chart-cell',
      renderCell: (params) => (
        <BarChart
          recordGroup={Types.RecordGroup.TRAINING}
          data={params.row.trainingStats}
        />
      ),
    },

    // DEPLOYMENTS BY MONTH
    deploymentsByMonth: {
      field: 'deploymentsByMonth',
      headerName: 'Deployments By Month',
      width: 232,
      cellClassName: 'chart-cell',
      renderCell: (params) => (
        <BarChart
          recordGroup={Types.RecordGroup.DEPLOYMENT}
          data={params.row.deploymentStats}
        />
      )
    },

    //  MENU
    menu: {
      field: 'menu',
      headerName: ' ',
      sortable: false,
      align: 'right',
      width: 42,
      minWidth: 42,
      renderCell: (params) => <Menu member={params.row} onRemove={onRemove} />
    }
  }


  const columnDefs = user.role.isSupervisor
    ? [
      fieldDefs.name,
      fieldDefs.lateRecordsCount,
      fieldDefs.trainingHoursCount,
      fieldDefs.deploymentsCount,
      fieldDefs.trainingByMonth,
      fieldDefs.deploymentsByMonth,
      fieldDefs.menu
    ] 
    : [
      fieldDefs.name,
      fieldDefs.lateRecordsCount,
      fieldDefs.eventsAttendedCount,
      fieldDefs.trainingHoursCount,
      fieldDefs.detectionExercisesCount,
      fieldDefs.patrolExercisesCount,
      fieldDefs.trainingByMonth,
      fieldDefs.menu
    ];


  return (
    <>
      {/* MOBILE - uses list items */}
      {isMobile &&
      <List>
        {data.map(member => <HandlerRowMobile 
          key={member.id} 
          member={member} 
          onRemove={onRemove} 
          isExpanded={isAllExpanded} 
        />)}
      </List>}

      {/* DESKTOP - uses data grid */}
      {!isMobile &&
      <>
        <div 
          style={{paddingLeft: user.role.isSupervisor ? '250px' : '330px'}}
          className='fake-header'
          children={<span>Last 3 Months</span>}
        />
        <DataGrid
          rows={data}
          columns={columnDefs}
          headerHeight={48}
          rowHeight={52}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter={data.length <= 100}
          hideFooterSelectedRowCount
        />
      </>}
    </>
  );
}